<template>
  <main role="main" class="py-4">
    <b-container class="container-x" style="max-width: 1000px;">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-4 border-bottom pb-2">
        <h4 class="m-0 text-dark font-weight-bold">{{ $t("createSubPlan") }}</h4>
      </div>

      <!-- Config Card -->
      <b-card class="shadow-sm border">
        <b-form class="mt-2" @submit.prevent="debouncedOnsubmitForm">
          
          <!-- Section 1: General Info -->
          <div class="mb-4">
            <div class="mb-3">
              <h6 class="text-primary font-weight-bold m-0">{{ $t("generalInfo") }}</h6>
            </div>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t('managementType') }}
                </template>
                <b-form-select v-model="managementType" :options="managementTypeOptions" @change="onManagementTypeChange" />
              </b-form-group>
              <div class="col-lg-7 mb-3 d-flex align-items-center" style="margin-top: 32px; height: 38px;">
                <label class="col-form-label font-weight-bold text-secondary small mr-3 mb-0 pt-0 pb-0">{{ $t('activeStatus') }}</label>
                <b-form-radio-group required :options="activeOptions" v-model="active" inline />
              </div>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("subPlanCode") }} <span class="text-danger">*</span>
                </template>
                <b-form-input type="text" minlength="1" maxlength="30" v-model="code" />
              </b-form-group>
              <div class="col-lg-7 mb-3">
                <b-form-row>
                  <b-form-group class="col-8 mb-0" label-class="font-weight-bold text-secondary small">
                    <template v-slot:label>
                      {{ $t("subPlanName") }} <span class="text-danger">*</span>
                    </template>
                    <b-form-input type="text" required minlength="2" maxlength="200" v-model="name" />
                  </b-form-group>
                  <b-form-group class="col-4 mb-0" :label="$t('packageType')" label-class="font-weight-bold text-secondary small">
                    <b-form-select v-model="packageType" :options="packageTypeOptions" />
                  </b-form-group>
                </b-form-row>
              </div>
            </b-form-row>

            <b-form-row v-if="managementType === enumManagementType.CONCURRENT_PARTICIPANT_POOL">
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("maxParticipantPool") }} <span class="text-danger">*</span>
                </template>
                <b-form-input required type="number" min="1" max="100000" v-model="maxParticipantPool" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100"
                  v-model="maxParticipantPool"
                  :options="maxParticipantPoolOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row v-if="managementType === enumManagementType.CONCURRENT_MEETING">
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("maximumNumberOfParticipantsInMeeting") }} <span class="text-danger">*</span>
                </template>
                <b-form-input required type="number" min="2" max="1000"
                  v-model="maximumNumberOfParticipantsInMeeting" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100"
                  v-model="maximumNumberOfParticipantsInMeeting"
                  :options="maximumNumberOfParticipantsInMeetingOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row v-if="managementType === enumManagementType.CONCURRENT_MEETING">
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("numberOfConcurrentMeetings") }} <span class="text-danger">*</span>
                </template>
                <b-form-input required type="number" min="1" max="100" v-model="maxConcurrentMeetings" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100"
                  v-model="maxConcurrentMeetings" :options="maxConcurrentMeetingsOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("maximumLengthOfOneMeeting") }} ({{ $t("minutes") }}) <span class="text-danger">*</span>
                </template>
                <b-form-input required type="number" min="1" max="1440" v-model="maxMeetingDuration" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100" v-model="maxMeetingDuration"
                  :options="maxMeetingDurationOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("numberOfMeetingsPerMonth") }} <span class="text-danger">*</span>
                </template>
                <b-form-input required type="number" min="1" max="100000" v-model="numberOfMeetingsPerMonth" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100"
                  v-model="numberOfMeetingsPerMonth" :options="numberOfMeetingsPerMonthOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("servicePrice") }} (VND) <span class="text-danger">*</span>
                </template>
                <b-form-input type="number" required min="0" max="1000000000" v-model="price" />
              </b-form-group>
              <div class="col-lg-7 mb-3">
                <b-form-row>
                  <b-form-group class="col-8 mb-0" label-class="font-weight-bold text-secondary small">
                    <template v-slot:label>
                      {{ $t("subscriptionLength") }} <span class="text-danger">*</span>
                    </template>
                    <b-form-input type="number" required min="1" max="32" v-model="numberOfDaysUsage" />
                  </b-form-group>
                  <b-form-group class="col-4 mb-0" :label="$t('unitTime')" label-class="font-weight-bold text-secondary small">
                    <b-form-select required :options="unitTimes" v-model="unitTime" />
                  </b-form-group>
                </b-form-row>
              </div>
            </b-form-row>
          </div>

          <!-- Section 2: Capacity -->
          <div class="mb-4 border-top pt-3">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("capacity") }}</h6>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("storageCapacity") }} (GB)
                </template>
                <b-form-input required type="number" min="0" max="100000" v-model="storageCapacity" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100" v-model="storageCapacity"
                  :options="storageCapacityOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("storageTime") }}
                </template>
                <b-form-input required type="number" min="1" max="100000" v-model="timeStorage" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100" v-model="timeStorage"
                  :options="timeStorageOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" :label="$t('storageType')" label-class="font-weight-bold text-secondary small">
                <b-form-select v-model="storageType" :options="storageTypeOptions" />
              </b-form-group>
            </b-form-row>

            <b-form-row>
              <b-form-group class="mb-2 sumarize">
                {{ storageSummary }}
              </b-form-group>
            </b-form-row>
          </div>

          <!-- Section 3: AI quota -->
          <div class="mb-4 border-top pt-3">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("ai_quota") }}</h6>

            <b-form-row>
              <b-form-group class="col-lg-5 mb-3" :label="$t('type')" label-class="font-weight-bold text-secondary small">
                <b-form-select v-model="aiQuotaType" :options="aiQuotaTypeOptions" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <div class="d-flex align-items-center" style="height: 38px;">
                  <span class="text-primary font-weight-bold">{{ aiQuotaDescription }}</span>
                </div>
              </b-form-group>
            </b-form-row>

            <b-form-row v-if="Number(aiQuotaType) !== enumAiQuotaType.Unlimited && Number(aiQuotaType) !== enumAiQuotaType.NoUse">
              <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
                <template v-slot:label>
                  {{ $t("aiQuotaDurationMinutes") }}
                </template>
                <b-form-input required type="number" min="0" max="100000" v-model="aiQuotaDuration" />
              </b-form-group>
              <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
                <b-form-radio-group buttons button-variant="outline-primary" class="w-100" v-model="aiQuotaDuration"
                  :options="aiQuotaDurationOptions" />
              </b-form-group>
            </b-form-row>
          </div>

          <!-- Section 4: Feature Settings -->
          <div class="mb-4 border-top pt-3">
            <h6 class="text-primary font-weight-bold mb-3">{{ $t("featureSettings") }}</h6>
            <div class="border rounded p-3 bg-light" style="max-height: 250px; overflow-y: auto;">
              <div class="col-12" v-for="item in featureList" :key="item.id">
                <b-form-checkbox v-model="item.isSelected" class="mb-1">
                  <span v-b-tooltip.bottom.hover :title="item.description" class="d-inline-block text-wrap-custom">{{
                    item.name
                  }}</span>
                </b-form-checkbox>
              </div>
            </div>
          </div>

          <!-- Footer Area -->
          <div class="d-flex flex-row align-items-center justify-content-end border-top pt-3 mt-4">
            <b-button type="button" variant="outline-secondary" class="mr-2" style="min-width: 120px;" @click="goBackToList">
              {{ $t("cancel") || 'Hủy' }}
            </b-button>
            <b-button type="submit" variant="primary" :disabled="isSubmitting" style="min-width: 140px;">
              <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
              {{ $t("createSubPlan") }}
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.container-x {
  max-width: 1000px;
}
.card {
  border-radius: 8px;
  background-color: #f4f9ff;
}
.font-weight-bold {
  font-weight: 600 !important;
}
.text-wrap-custom {
  max-width: 100%;
  white-space: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
}
.sumarize {
  color: #004F9E;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  padding-left: 10px;
  padding-top: 10px;
}
</style>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import _ from "lodash";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import BaseComponent from "@/components/BaseComponent";
import * as featureConfigService from "@/service/featureConfigService";
import { Status } from '../enums/EnableStatus';
import { StorageType } from '../enums/storageType';
import { ManagementType } from '../enums/subscriptionAction';
import { gbToBytes } from '@/service/util';
import { BounceTimes } from '../enums/TimesNumbers';
import { AiQuotaType, aiQuotaDurationOptions } from '../enums/aiQuotaType';

@Component
export default class NewSubscriptionPlan extends BaseComponent {

  enumAiQuotaType = AiQuotaType;
  enumManagementType = ManagementType;

  managementType = ManagementType.CONCURRENT_MEETING;
  maxParticipantPool: number = null;
  maxParticipantPoolOptions = [100, 200, 300, 500, 1000];

  name = "";
  code = "";
  packageType = "Basic";
  packageTypeOptions = [];
  maximumNumberOfParticipantsInMeetingOptions = [50, 100, 300, 500, 1000];
  maximumNumberOfParticipantsInMeeting: number = null;
  maxConcurrentMeetingsOptions = [10, 20, 30, 50, 100];
  maxConcurrentMeetings: number = null;
  maxMeetingDuration: number = null;
  numberOfMeetingsPerMonthOptions = [100, 200, 300, 500, 1000];
  numberOfMeetingsPerMonth: number = null;
  price: number = null;
  numberOfDaysUsage = 1;
  unitTime = "month";
  coreImplNameOptions = [
    { text: "BBB (BigBlueButton)", value: "bbb" },
    { text: "Zoom", value: "zoom" },
  ];
  coreImplName = "bbb";
  active = Status.ENABLE;

  // Dung lượng lưu trữ 1G 2G 5G 10G 20G
  storageCapacity: number = null;
  storageCapacityOptions = [
    { text: "1GB", value: 1 },
    { text: "2GB", value: 2 },
    { text: "5GB", value: 5 },
    { text: "10GB", value: 10 },
    { text: "20GB", value: 20 },
  ];
  // Thời gian lưu trữ
  timeStorage: number = null;
  // Phân loại lưu trữ
  storageType = StorageType.Fixed;

  get managementTypeOptions() {
    return [
      { value: ManagementType.CONCURRENT_MEETING, text: this.$t("numberOfConcurrentMeetings").toString() },
      { value: ManagementType.CONCURRENT_PARTICIPANT_POOL, text: this.$t("concurrentParticipantPoolOption").toString() }
    ];
  }

  get packageStatusOptions() {
    return [
      { value: 1, text: this.$t("demo").toString() },
      { value: 2, text: this.$t("trial").toString() },
      { value: 3, text: this.$t("testing").toString() },
      { value: 4, text: this.$t("official").toString() },
    ];
  }

  get maxMeetingDurationOptions() {
    return [
      { text: "2 " + this.$t("hours").toString(), value: 120 },
      { text: "4 " + this.$t("hours").toString(), value: 240 },
      { text: "8 " + this.$t("hours").toString(), value: 480 },
      { text: "10 " + this.$t("hours").toString(), value: 600 },
      { text: "24 " + this.$t("hours").toString(), value: 1440 }
    ];
  }

  get numberOfDaysUsageOptions() {
    return [
      { text: "1 " + this.$t("months").toString(), value: 1 },
      { text: "3 " + this.$t("months").toString(), value: 3 },
      { text: "6 " + this.$t("months").toString(), value: 6 },
      { text: "12 " + this.$t("months").toString(), value: 12 },
      { text: "24 " + this.$t("months").toString(), value: 24 },
      { text: "36 " + this.$t("months").toString(), value: 36 },
    ];
  }

  get unitTimes() {
    return [
      { text: this.$t("days").toString(), value: "day" },
      { text: this.$t("weeks").toString(), value: "week" },
      { text: this.$t("months").toString(), value: "month" },
      { text: this.$t("years").toString(), value: "year" }
    ];
  }

  get activeOptions() {
    return [
      { text: this.$t("yes").toString(), value: Status.ENABLE },
      { text: this.$t("no").toString(), value: Status.DISABLE },
    ];
  }

  get timeStorageOptions() {
    return [
      { text: "1 " + this.$t("months").toString(), value: 30 },
      { text: "2 " + this.$t("months").toString(), value: 60 },
      { text: "3 " + this.$t("months").toString(), value: 90 },
      { text: "6 " + this.$t("months").toString(), value: 180 },
    ];
  }

  get storageTypeOptions() {
    const options = [
      { value: StorageType.Fixed, text: this.$t("storageType_Fix").toString() },
      { value: StorageType.Session, text: this.$t("storageType_Session").toString() }
    ];
    if (this.managementType === 'CONCURRENT_MEETING') {
      options.push({ value: StorageType.ConcurrentMeeting, text: this.$t("storageType_Meeting").toString() });
    }
    return options;
  }

  onManagementTypeChange() {
    if (this.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL) {
      this.maxConcurrentMeetings = null;
      if (this.storageType === StorageType.ConcurrentMeeting) {
        this.storageType = StorageType.Fixed;
      }
    }
  }

  // Hạn mức AI
  aiQuotaType = AiQuotaType.NoUse;
  aiQuotaDuration: number = 500;
  aiQuotaDurationOptions = aiQuotaDurationOptions;

  isSubmitting = false;
  featureList = [];
  subscriptionPlanId: number;
  // tổng số chức năng
  featureTotal: number = null;

  mounted() {
    this.fetchPlanTypeList();
    this.fetchFeatureList();
  }
  // Get 1000 items without pagination to display on the interface.
  async fetchFeatureList() {
    try {
      const res = await featureConfigService.getAll(1, 1000);
      this.featureList = this.sortedFeatures(res.data);
      this.featureTotal = res.total;
    } catch (e) {
      this.toastError(e);
    }
  }

  async fetchPlanTypeList() {
    try {
      this.packageTypeOptions = await subscriptionPlanService.getPlanTypeList();
    } catch (e) {
      this.toastError(e);
    }
  }
  debouncedOnsubmitForm = _.debounce(() => {
      this.submitForm ();
    }, BounceTimes.BOUNCETIMES);
  async submitForm() {
    try {

      this.isSubmitting = true;

      const filterSubcription = this.featureList
        .filter(f => f.isSelected)
        .map(f => ({
          featureId: f.id,
        }));

      if (filterSubcription.length == 0) {
        this.toastErrorMessage(this.$t("errorNullSubcription"));
        return;
      }
      // Check storage
      if (this.storageCapacity == null) {
        this.toastErrorMessage(this.$t("storageCapacityNotAllowNull"));
        return;
      }
      //
      if (this.timeStorage == null) {
        this.toastErrorMessage(this.$t("storageTimeNotAllowNull"));
        return;
      }
      // Check participant pool if pool package
      if (this.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL && this.maxParticipantPool == null) {
        this.toastErrorMessage(this.$t("maxParticipantPoolRequired"));
        return;
      }
      let maxSize = 0;
      if (this.managementType === ManagementType.CONCURRENT_MEETING && this.storageType == StorageType.ConcurrentMeeting) {
        maxSize = (this.maxConcurrentMeetings || 0) * this.storageCapacity;
      } else {
        maxSize = this.storageCapacity;
      }
      const data = {
        name: this.name,
        packageType: this.packageType,
        // status: this.status,
        maximumNumberOfParticipantsInMeeting: this.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL
          ? +this.maxParticipantPool
          : +this.maximumNumberOfParticipantsInMeeting,
        numberOfConcurrentMeetings: this.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL
          ? null
          : +this.maxConcurrentMeetings,
        managementType: this.managementType,
        maxParticipantPool: this.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL
          ? +this.maxParticipantPool
          : null,
        maximumLengthOfOneMeeting: +this.maxMeetingDuration,
        numberOfMeetingsPerMonth: +this.numberOfMeetingsPerMonth,
        price: +this.price,
        numberOfDaysUsage: +this.numberOfDaysUsage,
        coreImplName: this.coreImplName,
        active: this.active,
        unitTime: this.unitTime,
        code: this.code,
        storage: {
          maxSize: gbToBytes(maxSize), /// Dung lượng tối đa lưu trữ
          type: this.storageType, /// phân loại lưu trữ: 1: cố định, 2: Phiên, 3 : theo số cuộc họp đồng thời
          retention: this.timeStorage  // thời gian lưu trữ (ngày)
        },
        aiQuota: {
          minutes: (Number(this.aiQuotaType) === AiQuotaType.Unlimited || Number(this.aiQuotaType) === AiQuotaType.NoUse) ? null : +this.aiQuotaDuration,
          type: Number(this.aiQuotaType)
        }

      };
      const res = await subscriptionPlanService.addNewPackage(data);
      const newId = res?.id || res?.data?.id;
      this.subscriptionPlanId = newId;
      this.addFeaturesToSubscriptionPlan(filterSubcription);
    } catch (e) {
      this.toastError(e);
    } finally {
      this.isSubmitting = false;
    }
  }

  //api truyền cấu hình chức năng
  async addFeaturesToSubscriptionPlan(filterSubcription) {
    try {
      const payload = {
        subscriptionPlanId: this.subscriptionPlanId,
        features: filterSubcription
      };
      await featureConfigService.addFeaturesToSubscriptionPlan(payload);
      this.goBackToList();
      this.toastSuccessDelay(this.$t("createSubPlanSuccessful").toString());
    } catch (e) {
      this.toastErrorOldType(e);
    }
  }

  goBackToList() {
    this.$router.push("/subscription-plans");
  }
  //
  get storageSummary() {

    if (this.storageCapacity == null || this.timeStorage == null) return "";

    let displayCapacity = `${this.storageCapacity}`;
    displayCapacity = this.storageCapacity + "GB";

    if (this.storageType === StorageType.Session) {
      displayCapacity = this.storageCapacity + "GB" + " / 1 " + this.$t("meeting").toString().toLowerCase();
    }

    else if (this.storageType === StorageType.ConcurrentMeeting) {
      if (this.maxConcurrentMeetings === 0) return;
      displayCapacity = (this.maxConcurrentMeetings * this.storageCapacity).toString() + "GB";
    }


    return this.$t('storage_Select_Sum', {
      capacity: displayCapacity,
      days: this.timeStorage
    });
  }

  get aiQuotaTypeOptions() {
    return [
      { value: AiQuotaType.NoUse, text: this.$t("aiQuotaType_NoUse").toString() },
      { value: AiQuotaType.Fixed, text: this.$t("aiQuotaType_Fixed").toString() },
      { value: AiQuotaType.NoReset, text: this.$t("aiQuotaType_NoReset").toString() },
      { value: AiQuotaType.Unlimited, text: this.$t("aiQuotaType_Unlimited").toString() }
    ];
  }

  get aiQuotaDescription() {
    switch (Number(this.aiQuotaType)) {
      case AiQuotaType.NoUse:
        return "";
      case AiQuotaType.Fixed:
        return this.$t("aiQuotaPeriodCycle").toString();
      case AiQuotaType.NoReset:
        return this.$t("aiQuotaNoResetDescription").toString();
      case AiQuotaType.Unlimited:
        return this.$t("aiQuotaUnlimitedDescription").toString();
      default:
        return "";
    }
  }
}
</script>